import { API_BASE as BASE_URL, baseHeaders, getAuthHeaders, apiFetch } from './api-fetch';

export interface ViewRecord {
  id: string;
  propertyId: string;
  duration: number; // in seconds
  viewerType: 'anonymous' | 'buyer' | 'agent';
  sessionId: string | null;
  timestamp: string;
  date: string;
}

export interface PropertyViewStats {
  totalViews: number;
  totalViewTime: number; // in seconds
  averageViewTime: number; // in seconds
  views: ViewRecord[];
}

export interface AgentViewStats {
  totalViews: number;
  totalViewTime: number; // in seconds
  averageViewTime: number; // in seconds
  propertyCount: number;
  propertyStats: {
    propertyId: string;
    propertyTitle: string;
    viewCount: number;
    totalViewTime: number;
    averageViewTime: number;
  }[];
}

// Record a property view
export async function recordPropertyView(
  propertyId: string,
  duration: number,
  viewerType: 'anonymous' | 'buyer' | 'agent' = 'anonymous',
  sessionId?: string
): Promise<{ success: boolean; viewId: string }> {
  try {
    const response = await apiFetch(`${BASE_URL}/property-views`, {
      method: 'POST',
      headers: baseHeaders,
      body: JSON.stringify({
        propertyId,
        duration: Math.round(duration), // Round to nearest second
        viewerType,
        sessionId: sessionId || null,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to record property view');
    }

    return await response.json();
  } catch (error) {
    console.error('Error recording property view:', error);
    throw error;
  }
}

// Get viewing statistics for a specific property
export async function getPropertyViewStats(propertyId: string): Promise<PropertyViewStats> {
  try {
    const response = await apiFetch(`${BASE_URL}/property-views/${propertyId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch property view stats');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching property view stats:', error);
    throw error;
  }
}

// Get aggregate viewing statistics for all properties
export async function getAgentViewStats(): Promise<AgentViewStats> {
  try {
    const response = await apiFetch(`${BASE_URL}/agent-view-stats`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      let message = 'Failed to fetch agent view stats';
      try { const error = await response.json(); message = error.error || message; } catch {}
      throw new Error(message);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching agent view stats:', error);
    throw error;
  }
}

// Format seconds to human-readable time
export function formatViewTime(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
}
